import type { Role } from '@iot-dashboard/shared';

export interface AuthUser {
  sub: string;
  email: string;
  role: Role;
  isMsp: boolean;
  tenantId?: string;
  groups: string[];
}

/** Secret and otpauth:// URI for registering an authenticator app. */
export interface TotpSetup {
  secret: string;
  uri: string;
}

export type SignInOutcome =
  | { kind: 'done' }
  | { kind: 'new-password' }
  | { kind: 'totp' }
  | { kind: 'totp-setup'; setup: TotpSetup }
  | { kind: 'unsupported'; step: string };

export interface MfaStatus {
  totpEnabled: boolean;
}

/** What an authentication backend (Cognito, or the demo stub) must provide. */
export interface AuthBackend {
  getUser(): Promise<AuthUser | null>;
  signIn(email: string, password: string): Promise<SignInOutcome>;
  confirmNewPassword(password: string): Promise<SignInOutcome>;
  confirmTotp(code: string): Promise<SignInOutcome>;
  signOut(): Promise<void>;
  requestPasswordReset(email: string): Promise<void>;
  confirmPasswordReset(email: string, code: string, newPassword: string): Promise<void>;
  getIdToken(): Promise<string | null>;
  /** Whether the signed-in user has an authenticator app registered. */
  getMfaStatus(): Promise<MfaStatus>;
  /** Starts authenticator enrolment for the signed-in user. */
  setupTotp(): Promise<TotpSetup>;
  /** Confirms enrolment with a code from the app and makes TOTP the preferred factor. */
  verifyTotpSetup(code: string): Promise<void>;
  disableTotp(): Promise<void>;
}

export interface AuthContextValue extends Omit<AuthBackend, 'getUser'> {
  status: 'loading' | 'signed-out' | 'signed-in';
  user: AuthUser | null;
  refresh(): Promise<void>;
}

export function roleFromGroups(groups: string[]): Role | null {
  if (groups.includes('msp-admin')) return 'msp-admin';
  if (groups.includes('tenant-admin')) return 'tenant-admin';
  if (groups.includes('tenant-viewer')) return 'tenant-viewer';
  return null;
}
